import {isGoalsReflectionQuestion} from '../create-checkin-cycle/components/goals-content';
import {GoalProgress} from '@utils/proportions';

export const GOAL_REFLECTION_SNAPSHOT_HEADING =
  '*Goals at the time of submission*';
export const GOAL_REFLECTION_SNAPSHOT_DIVIDER = '\n\n---\n\n';

const escapeMarkdown = (value: string) =>
  value.replace(/([\\`*_[\]])/g, '\\$1');

export const getCheckinGoalProgress = (goal: any) => {
  const value = Number(GoalProgress(goal).percentage);
  return Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : 0;
};

export const hasGoalReflectionSnapshot = (response?: string | null) =>
  Boolean(response?.trim().startsWith(GOAL_REFLECTION_SNAPSHOT_HEADING));

export const splitGoalReflectionResponse = (response?: string | null) => {
  const normalized = response || '';
  if (!hasGoalReflectionSnapshot(normalized)) {
    return {snapshot: '', answer: normalized};
  }

  const dividerIndex = normalized.indexOf(GOAL_REFLECTION_SNAPSHOT_DIVIDER);
  if (dividerIndex < 0) return {snapshot: normalized.trim(), answer: ''};

  return {
    snapshot: normalized.slice(0, dividerIndex).trim(),
    answer: normalized
      .slice(dividerIndex + GOAL_REFLECTION_SNAPSHOT_DIVIDER.length)
      .trim(),
  };
};

export const combineGoalReflectionResponse = (
  snapshot: string,
  answer?: string | null,
) => {
  const normalizedSnapshot = snapshot.trim();
  const normalizedAnswer = (answer || '').trim();
  if (!normalizedSnapshot) return normalizedAnswer;
  return normalizedAnswer
    ? `${normalizedSnapshot}${GOAL_REFLECTION_SNAPSHOT_DIVIDER}${normalizedAnswer}`
    : normalizedSnapshot;
};

export const buildGoalReflectionSnapshot = (goals: any[]) => {
  const goalLines = goals.map((goal) => {
    const name = escapeMarkdown(String(goal?.name || 'Untitled goal'));
    const progress = Math.round(getCheckinGoalProgress(goal));
    return `- *${name} — ${progress}% complete*`;
  });

  return [GOAL_REFLECTION_SNAPSHOT_HEADING, '', ...goalLines].join('\n');
};

export const addGoalReflectionSnapshot = <
  T extends {
    questions?: {
      questions?: Array<{
        question?: string;
        response?: string | number | null;
        [key: string]: unknown;
      }>;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  }
>(
  values: T,
  goals: any[],
): T => {
  if (!goals.length || !values.questions?.questions?.length) return values;

  let changed = false;
  const questions = values.questions.questions.map((question) => {
    if (!isGoalsReflectionQuestion(question.question)) return question;

    const response =
      question.response === null || question.response === undefined
        ? ''
        : String(question.response);
    if (hasGoalReflectionSnapshot(response)) return question;

    changed = true;
    const snapshot = buildGoalReflectionSnapshot(goals);
    const answer = response.trim();

    return {
      ...question,
      response: combineGoalReflectionResponse(snapshot, answer),
    };
  });

  if (!changed) return values;

  return {
    ...values,
    questions: {
      ...values.questions,
      questions,
    },
  };
};
